import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import Table from './Table';
import RequestLogBadge from './RequestLogBadge';
import { DateTime } from 'luxon';
import formatDate from '../helpers/formatDate';

export default function LeadsTable() {
  const [ffData, setFFData] = useState([]);
  const [brokerData, setBrokerData] = useState([]);
  const [leadOwnerData, setLeadOwnerData] = useState([]);
  const [loadingFFData, setLoadingFFData] = useState(false);
  const [xubspotData, setHubspotData] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loadTime, setLoadTime] = useState(null);
  const [requestLog, setRequestLog] = useState([]);
  const [requestLogModalOpen, setRequestLogModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [jsonModal, setJsonModal] = useState(null);
  const [successToastMessage, setSuccessToastMessage] = useState(null);
  const [syncingRecordIds, setSyncingRecordIds] = useState([]);
  const [bulkSyncConfirmation, setBulkSyncConfirmation] = useState({
    isOpen: false,
    records: [],
  });
  const [bulkSyncState, setBulkSyncState] = useState({
    isOpen: false,
    isRunning: false,
    total: 0,
    completed: 0,
    currentLabel: null,
    failures: [],
  });
  const [bulkTimestampSyncConfirmation, setBulkTimestampSyncConfirmation] = useState({
    isOpen: false,
    records: [],
  });
  const [bulkTimestampSyncState, setBulkTimestampSyncState] = useState({
    isRunning: false,
  });
  const [syncPreview, setSyncPreview] = useState({
    isOpen: false,
    isLoading: false,
    isSaving: false,
    record: null,
    fields: null,
    isNewRecord: false,
    error: null,
  });
  const activeSyncKeysRef = useRef(new Set());
  const bulkSyncRunningRef = useRef(false);
  const tableInstanceRef = useRef(null);

  // Toggles the Sync Status column filter — clicking an already-active status
  // clears it. Reads the current value at click time via the table instance
  // captured from renderToolbar, so it doesn't need filter state lifted into
  // this component just to stay in sync.
  const toggleSyncStatusFilter = useCallback((status) => {
    const column = tableInstanceRef.current?.getColumn('sync_status');
    if (!column) return;

    column.setFilterValue((prev) => (prev === status ? undefined : status));
  }, []);

  const getURLParams = () => new URLSearchParams(window.location.search);

  const getStartDate = () =>
    getURLParams().get('start_date') || DateTime.now().startOf('day').toISO().slice(0, 16);

  const getEndDate = () =>
    getURLParams().get('end_date') || DateTime.now().startOf('day').plus({ days: 1 }).toISO().slice(0, 16);

  // The backend still receives full start_date/end_date datetime params (start of
  // day / start of next day) — this just derives a single day value for the picker.
  const getSelectedDate = () => DateTime.fromISO(getStartDate()).toFormat('yyyy-MM-dd');

  const setDateParams = (start, end) => {
    const params = new URLSearchParams(window.location.search);
    params.set('start_date', start);
    params.set('end_date', end);
    window.history.pushState({}, '', `?${params.toString()}`);
    setFilter(start + end);
  };

  const [filter, setFilter] = useState(getStartDate() + getEndDate());

  const refreshData = () => setFilter(getStartDate() + getEndDate() + Date.now());

  const hubspotData = xubspotData.map((item) => {
    const toAssociations = item.associations?.to || [];
    const toAssociation = toAssociations[0];
    const hsBrokerId = toAssociation?.id;
    const broker = hsBrokerId ? brokerData.find((b) => b.hubspot_id?.toString() === hsBrokerId) : null;
    const leadOwner = leadOwnerData.find((l) => l.hubspot_id?.toString() === item.properties.hubspot_owner_id);
    return {
      ...item,
      broker_name: broker?.display_name,
      lead_owner_name: leadOwner?.name || item.properties.hubspot_owner_id,
    };
  });

  useEffect(() => {
    setLoadingFFData(true);
    setLoadError(null);
    setFFData([]);
    setHubspotData([]);
    setLoadTime(null);
    setRequestLog([]);

    const startTime = Date.now();
    let cancelled = false;

    // Each request covers one date-bucket chunk of the range rather than the
    // whole thing (see leads_controller#index) — loop until every chunk has
    // been fetched, accumulating into local state as each one arrives.
    async function fetchAllPages() {
      const accumulatedFFData = [];
      const accumulatedHsData = [];
      const log = [];
      let offset = null;
      let hasMore = true;
      let requestNumber = 0;

      while (hasMore) {
        if (cancelled) break;

        const searchParams = new URLSearchParams({
          start_date: getStartDate(),
          end_date: getEndDate(),
        });

        if (offset) {
          searchParams.set('offset', offset);
        }

        requestNumber += 1;
        const currentRequestNumber = requestNumber;
        const requestStartedAt = Date.now();

        log.push({
          requestNumber: currentRequestNumber,
          offset: offset || 0,
          chunkIndex: null,
          totalChunks: null,
          bucketStart: null,
          bucketEnd: null,
          status: null,
          ok: null,
          pending: true,
          durationMs: null,
          scanned: null,
          returned: null,
          error: null,
        });
        if (!cancelled) setRequestLog([...log]);

        let response = null;
        let res = null;
        let errorMessage = null;

        try {
          response = await fetch(`api/leads?${searchParams.toString()}`);
          res = await response.json();
          if (!response.ok) {
            errorMessage = `HTTP ${response.status}`;
          }
        } catch (err) {
          errorMessage = err.message;
        }

        const entryIndex = log.findIndex((entry) => entry.requestNumber === currentRequestNumber);
        if (entryIndex !== -1) {
          log[entryIndex] = {
            requestNumber: currentRequestNumber,
            offset: offset || 0,
            chunkIndex: res?.meta?.chunk_index,
            totalChunks: res?.meta?.total_chunks,
            bucketStart: res?.meta?.bucket_start,
            bucketEnd: res?.meta?.bucket_end,
            status: response ? response.status : null,
            ok: !errorMessage,
            pending: false,
            durationMs: Date.now() - requestStartedAt,
            scanned: res?.meta?.scanned_count,
            returned: res?.meta?.returned,
            error: errorMessage,
          };
        }
        if (!cancelled) setRequestLog([...log]);

        if (errorMessage) {
          throw new Error(`Failed to load leads (${errorMessage})`);
        }

        accumulatedFFData.push(...(res.data || []));
        accumulatedHsData.push(...(res.hubspot_data || []));

        if (!cancelled) {
          setFFData([...accumulatedFFData]);
          setHubspotData([...accumulatedHsData]);
          setBrokerData(res.broker_data || []);
          setLeadOwnerData(res.lead_owner_data || []);
        }

        offset = res.meta?.next_offset;
        hasMore = Boolean(res.meta?.has_more && offset);
      }
    }

    fetchAllPages()
      .then(() => {
        if (!cancelled) {
          setLoadTime(((Date.now() - startTime) / 1000).toFixed(2));
          setLoadingFFData(false);
        }
      })
      .catch((error) => {
        console.error(error);

        if (!cancelled) {
          setLoadTime(((Date.now() - startTime) / 1000).toFixed(2));
          setLoadError('Unable to load lead records for the selected time window.');
          setLoadingFFData(false);
        }
      });

    return () => { cancelled = true; };
  }, [filter]);

  const getRecordKey = useCallback((record) => {
    if (record.ff_record?.id) return `ff-${record.ff_record.id}`;
    if (record.hs_record?.id) return `hs-${record.hs_record.id}`;
    if (record.ff_record?.hubspot_id) return `hubspot-${record.ff_record.hubspot_id}`;
    return 'unknown-record';
  }, []);

  const getRecordLabel = useCallback((record) => {
    return (
      record.ff_record?.dealName ||
      record.hs_record?.properties?.dealname ||
      record.ff_record?.emailId ||
      record.hs_record?.properties?.candidate_email ||
      record.ff_record?.hubspot_id ||
      record.hs_record?.id ||
      'Unknown lead'
    );
  }, []);

  const canSyncRecord = useCallback((record) => {
    return ['synced', 'out_of_sync', 'out_of_sync_timestamp_only', 'hs_only'].includes(record.sync_status) && Boolean(record.hs_record?.id);
  }, []);

  // mergeUpdatedRecord queues updates and flushes them in a single batched pass
  // instead of updating state per-record — doing a full .map() over ffData (plus
  // the downstream combinedData/statusCounts/table-internals recompute it
  // triggers) for every individual sync completion gets slow with a large
  // dataset loaded, especially during bulk sync which streams in many
  // completions. Batching collapses rapid completions into one pass.
  const pendingMergeQueueRef = useRef([]);
  const mergeFlushScheduledRef = useRef(false);

  const flushMergeQueue = useCallback(() => {
    mergeFlushScheduledRef.current = false;
    const queued = pendingMergeQueueRef.current;
    if (queued.length === 0) return;
    pendingMergeQueueRef.current = [];

    const normalizedRecords = queued.map((updatedRecord) => (
      typeof updatedRecord === 'string' ? JSON.parse(updatedRecord) : updatedRecord
    ));

    setFFData((prev) => {
      const indexById = new Map();
      prev.forEach((lead, index) => {
        if (lead.id != null) indexById.set(`id-${lead.id}`, index);
        if (lead.hubspot_id != null) indexById.set(`hs-${lead.hubspot_id}`, index);
      });

      const next = [...prev];

      normalizedRecords.forEach((normalizedRecord) => {
        const idKey = normalizedRecord.id != null ? `id-${normalizedRecord.id}` : null;
        const hsKey = normalizedRecord.hubspot_id != null ? `hs-${normalizedRecord.hubspot_id}` : null;
        const existingIndex = (idKey && indexById.get(idKey)) ?? (hsKey && indexById.get(hsKey));

        if (existingIndex != null) {
          next[existingIndex] = normalizedRecord;
        } else {
          next.push(normalizedRecord);
          if (idKey) indexById.set(idKey, next.length - 1);
          if (hsKey) indexById.set(hsKey, next.length - 1);
        }
      });

      return next;
    });
  }, []);

  const mergeUpdatedRecord = useCallback((updatedRecord) => {
    pendingMergeQueueRef.current.push(updatedRecord);

    if (!mergeFlushScheduledRef.current) {
      mergeFlushScheduledRef.current = true;
      setTimeout(flushMergeQueue, 0);
    }
  }, [flushMergeQueue]);

  const performSyncRequest = useCallback(async (record, options = {}) => {
    const { showSuccessToast = false, showErrorModal = false } = options;

    try {
      // Send the already-loaded HubSpot data so the backend can sync from it directly
      // instead of doing another live HubSpot fetch. Falls back server-side to a live
      // fetch if this is missing or incomplete.
      const response = await fetch(`api/leads/${record.hs_record.id}/sync_with_hubspot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_record: record.hs_record }),
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);
        if (responseData?.data) {
          mergeUpdatedRecord(responseData.data);
        }

        if (showSuccessToast) {
          setSuccessToastMessage('The lead was successfully synced with HubSpot.');
        }

        return { success: true };
      }

      const errorData = await response.json().catch(() => null);
      const message = errorData?.errors?.join('\n') || `Sync failed (HTTP ${response.status})`;

      if (showErrorModal) {
        setModalMessage(message);
      }

      return { success: false, message };
    } catch (error) {
      console.error('Sync error:', error);
      const message = 'An unexpected error occurred while syncing.';

      if (showErrorModal) {
        setModalMessage(message);
      }

      return { success: false, message };
    }
  }, [mergeUpdatedRecord]);

  const sendSyncRequest = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return null;

    const recordKey = getRecordKey(record);
    if (activeSyncKeysRef.current.has(recordKey)) return null;

    activeSyncKeysRef.current.add(recordKey);
    setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));

    try {
      return await performSyncRequest(record, { showSuccessToast: true, showErrorModal: true });
    } finally {
      flushMergeQueue();
      activeSyncKeysRef.current.delete(recordKey);
      setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
    }
  }, [getRecordKey, performSyncRequest, flushMergeQueue]);

  const openSyncPreview = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return;

    setSyncPreview({
      isOpen: true,
      isLoading: true,
      isSaving: false,
      record,
      fields: null,
      isNewRecord: false,
      error: null,
    });

    try {
      const response = await fetch(`api/leads/${record.hs_record.id}/sync_preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_record: record.hs_record }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const message = responseData?.errors?.join('\n') || `Failed to load preview (HTTP ${response.status})`;
        setSyncPreview((prev) => ({ ...prev, isLoading: false, error: message }));
        return;
      }

      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        fields: responseData?.fields || [],
        isNewRecord: Boolean(responseData?.is_new_record),
      }));
    } catch (error) {
      console.error('Sync preview error:', error);
      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        error: 'An unexpected error occurred while loading the preview.',
      }));
    }
  }, []);

  const closeSyncPreview = useCallback(() => {
    setSyncPreview((prev) => (prev.isSaving ? prev : {
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
    }));
  }, []);

  const confirmSyncPreview = useCallback(async () => {
    const record = syncPreview.record;
    if (!record) return;

    setSyncPreview((prev) => ({ ...prev, isSaving: true }));
    await sendSyncRequest(record);
    flushMergeQueue();
    setSyncPreview({
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
    });
  }, [syncPreview.record, sendSyncRequest, flushMergeQueue]);

  const closeBulkSyncModal = useCallback(() => {
    setBulkSyncState((prev) => {
      if (prev.isRunning) return prev;

      return {
        ...prev,
        isOpen: false,
        currentLabel: null,
      };
    });
  }, []);

  const openBulkSyncConfirmation = useCallback((records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkSyncConfirmation({
      isOpen: true,
      records,
    });
  }, []);

  const closeBulkSyncConfirmation = useCallback(() => {
    setBulkSyncConfirmation({
      isOpen: false,
      records: [],
    });
  }, []);

  const startBulkSync = useCallback(async (records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkSyncState({
      isOpen: true,
      isRunning: true,
      total: records.length,
      completed: 0,
      currentLabel: null,
      failures: [],
    });

    let failureCount = 0;

    try {
      for (const record of records) {
        const recordKey = getRecordKey(record);
        const recordLabel = getRecordLabel(record);

        activeSyncKeysRef.current.add(recordKey);
        setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));
        setBulkSyncState((prev) => ({
          ...prev,
          currentLabel: recordLabel,
        }));

        const result = await performSyncRequest(record);

        if (!result?.success) {
          failureCount += 1;
          setBulkSyncState((prev) => ({
            ...prev,
            failures: [...prev.failures, {
              id: recordKey,
              label: recordLabel,
              message: result?.message || 'Unknown sync failure.',
            }],
          }));
        }

        activeSyncKeysRef.current.delete(recordKey);
        setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
        setBulkSyncState((prev) => ({
          ...prev,
          completed: prev.completed + 1,
        }));
      }
    } finally {
      flushMergeQueue();
      bulkSyncRunningRef.current = false;
      setBulkSyncState((prev) => ({
        ...prev,
        isRunning: false,
        currentLabel: null,
      }));
      setSuccessToastMessage(`Bulk sync finished. ${records.length - failureCount} succeeded, ${failureCount} failed.`);
    }
  }, [getRecordKey, getRecordLabel, performSyncRequest, flushMergeQueue]);

  const confirmBulkSync = useCallback(async () => {
    const records = bulkSyncConfirmation.records;
    closeBulkSyncConfirmation();
    await startBulkSync(records);
  }, [bulkSyncConfirmation.records, closeBulkSyncConfirmation, startBulkSync]);

  const openBulkTimestampSyncConfirmation = useCallback((records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkTimestampSyncConfirmation({
      isOpen: true,
      records,
    });
  }, []);

  const closeBulkTimestampSyncConfirmation = useCallback(() => {
    setBulkTimestampSyncConfirmation({
      isOpen: false,
      records: [],
    });
  }, []);

  // Sends every record's already-loaded hs_record in one request instead of one
  // request per record. The backend re-verifies (server-side) that each record is
  // still timestamp-only before writing anything, and — unlike #sync_with_hubspot —
  // touches only the lastUpdate column, not Lead::CreateOrUpdate's full field set,
  // so this can't create a Division.
  const startBulkTimestampSync = useCallback(async (records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkTimestampSyncState({ isRunning: true });

    try {
      const response = await fetch('api/leads/bulk_sync_timestamps', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_records: records.map((record) => record.hs_record) }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        setModalMessage(responseData?.errors?.join('\n') || `Timestamp sync failed (HTTP ${response.status})`);
        return;
      }

      const results = responseData?.results || [];
      const succeeded = results.filter((result) => result.success);
      const failed = results.filter((result) => !result.success);

      const timestampByHubspotId = new Map(
        succeeded.map((result) => [result.hubspot_id?.toString(), result.hs_lastmodifieddate])
      );

      if (timestampByHubspotId.size > 0) {
        setFFData((prev) => prev.map((lead) => {
          const newTimestamp = timestampByHubspotId.get(lead.hubspot_id?.toString());
          return newTimestamp ? { ...lead, lastUpdate: newTimestamp } : lead;
        }));

        setHubspotData((prev) => prev.map((hsRecord) => {
          const newTimestamp = timestampByHubspotId.get(hsRecord.id?.toString());
          if (!newTimestamp) return hsRecord;

          return {
            ...hsRecord,
            properties: { ...(hsRecord.properties || {}), hs_lastmodifieddate: newTimestamp },
          };
        }));
      }

      if (failed.length > 0) {
        setModalMessage(
          `${succeeded.length} timestamp(s) updated. ${failed.length} were skipped:\n` +
          failed.map((result) => `#${result.hubspot_id}: ${result.error}`).join('\n')
        );
      } else {
        setSuccessToastMessage(`${succeeded.length} timestamp(s) synced with HubSpot.`);
      }
    } catch (error) {
      console.error('Bulk timestamp sync error:', error);
      setModalMessage('An unexpected error occurred while syncing timestamps.');
    } finally {
      bulkSyncRunningRef.current = false;
      setBulkTimestampSyncState({ isRunning: false });
    }
  }, []);

  const confirmBulkTimestampSync = useCallback(async () => {
    const records = bulkTimestampSyncConfirmation.records;
    closeBulkTimestampSyncConfirmation();
    await startBulkTimestampSync(records);
  }, [bulkTimestampSyncConfirmation.records, closeBulkTimestampSyncConfirmation, startBulkTimestampSync]);

  useEffect(() => {
    if (!successToastMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessToastMessage(null);
    }, 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successToastMessage]);

  const humanizeDiffKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDiffValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">—</span>;
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : <span className="text-muted">—</span>;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const calculateSyncStatus = (ff_record, hs_record) => {
    if (!hs_record && ff_record) return 'ff_only';
    if (hs_record && !ff_record) return 'hs_only';
    const hs_value = formatDate(hs_record?.properties?.hs_lastmodifieddate);
    const ff_value = formatDate(ff_record?.lastUpdate);
    if (hs_value === ff_value) return 'synced';
    // Set server-side (see flag_timestamp_only_diffs): true when hs_lastmodifieddate
    // is the only thing that changed — HubSpot bumps it for lots of things we don't
    // track, so this isn't necessarily worth acting on the way a real change is.
    if (hs_record?.timestamp_only_diff) return 'out_of_sync_timestamp_only';
    return 'out_of_sync';
  };

  const ffDataWithHsRecord = ffData.map((lead) => {
    const matchingHubspotData = hubspotData.find((hs) => hs.id?.toString() === lead.hubspot_id?.toString());
    let hsParentRecord = null;
    if (!matchingHubspotData) {
      hsParentRecord = hubspotData.find((r) =>
        r.properties?.hs_merged_object_ids?.split(';').includes(lead.hubspot_id?.toString())
      );
    }

    const duplicateFFRecords = ffData.filter((r) => r.email === lead.email && r.agency?.name === lead.agency?.name);
    const ffDuplicateEmailRecords = ffData.filter((r) => r.email === lead.email);
    const hsDuplicateEmailRecords = hubspotData.filter((r) => r.properties?.email === lead.email);
    const duplicateEmailRecords = [...new Set([...ffDuplicateEmailRecords, ...hsDuplicateEmailRecords])];

    return {
      ff_record: lead,
      hs_record: matchingHubspotData,
      hs_merged_parent: hsParentRecord,
      has_duplicate_records: duplicateFFRecords.length > 1,
      has_duplicate_emails: duplicateEmailRecords.length > 1,
      sync_status: calculateSyncStatus(lead, matchingHubspotData),
    };
  });

  const matchingHsIds = ffData.map((r) => r.hubspot_id?.toString());
  const unmatchedHubspotData = hubspotData.filter((hs) => !matchingHsIds.includes(hs.id?.toString()));
  const formattedUnmatchedHubspotData = unmatchedHubspotData.map((hs) => {
    const duplicateFFRecords = ffData.filter(
      (r) => r.email === hs.properties?.email && r.agency?.name === hs.properties?.network
    );
    const ffDuplicateEmailRecords = ffData.filter((r) => r.email === hs.properties?.email);
    const hsDuplicateEmailRecords = hubspotData.filter((r) => r.properties?.email === hs.properties?.email);
    const duplicateEmailRecords = [...new Set([...ffDuplicateEmailRecords, ...hsDuplicateEmailRecords])];
    return {
      ff_record: null,
      hs_record: hs,
      hs_merged_parent: null,
      has_duplicate_records: duplicateFFRecords.length > 1,
      has_duplicate_emails: duplicateEmailRecords.length > 1,
      sync_status: 'hs_only',
    };
  });

  const combinedData = [...formattedUnmatchedHubspotData, ...ffDataWithHsRecord];
  const statusCounts = React.useMemo(() => {
    return combinedData.reduce((acc, row) => {
      acc[row.sync_status] = (acc[row.sync_status] || 0) + 1;
      return acc;
    }, {});
  }, [combinedData]);
  const bulkProgressPercent = bulkSyncState.total === 0
    ? 0
    : Math.round((bulkSyncState.completed / bulkSyncState.total) * 100);

  const columns = React.useMemo(
    () => [
      {
        accessorFn: (row) => row.ff_record?.id?.toString() || '',
        header: 'FF ID',
      },
      {
        accessorFn: (row) => row.ff_record?.hubspot_id?.toString() || row.hs_record?.id?.toString() || '',
        cell: (info) => (
          <div
            className="flex gap-2"
            style={{
              width: info.row.original.hs_merged_parent ? '150px' : 'auto',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{info.getValue()}</span>
            {info.row.original.hs_merged_parent && (
              <span className="badge bg-danger" style={{ color: 'white' }}>
                <div>HS Merged</div>
                <div>{info.row.original.hs_merged_parent.id}</div>
              </span>
            )}
          </div>
        ),
        header: 'HS ID',
      },
      {
        accessorFn: (row) => formatDate(row.ff_record?.lastUpdate),
        header: 'FF Version Timestamp',
      },
      {
        accessorFn: (row) => formatDate(row.hs_record?.properties?.hs_lastmodifieddate),
        header: 'HS Version Timestamp',
      },
      {
        id: 'sync_status',
        accessorFn: (row) => calculateSyncStatus(row.ff_record, row.hs_record),
        header: 'Sync Status',
        // Exact match — the default filter fn does substring matching, which would
        // make "out_of_sync" also match "out_of_sync_timestamp_only".
        filterFn: 'equalsString',
        meta: { filterVariant: 'select' },
      },
      {
        accessorFn: (row) => row.ff_record?.dealName || row.hs_record?.properties?.dealname,
        header: 'Deal Name',
      },
      {
        accessorFn: (row) => row.ff_record?.emailId || row.hs_record?.properties?.candidate_email,
        header: 'Email',
      },
      {
        accessorFn: (row) => row.ff_record?.leadStatusID || row.hs_record?.properties?.dealstage,
        header: 'Lead Status',
      },
      {
        accessorFn: (row) => row.ff_record?.division?.name || row.hs_record?.properties?.brand_name,
        header: 'Division',
        meta: { filterVariant: 'select' },
      },
      {
        accessorFn: (row) => row.ff_record?.broker?.display_name,
        header: 'FF Broker',
      },
      {
        accessorFn: (row) => row.hs_record?.broker_name || '',
        header: 'HS Broker',
      },
      {
        accessorFn: (row) => row.ff_record?.lead_owner?.name || row.hs_record?.lead_owner_name,
        header: 'Lead Owner',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const record = info.row.original;
          const isRowSyncing = syncingRecordIds.includes(getRecordKey(record));
          return (
            <div style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
              {canSyncRecord(record) && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => openSyncPreview(record)}
                  disabled={bulkSyncState.isRunning || isRowSyncing}
                >
                  {isRowSyncing ? 'Syncing...' : 'Sync Record'}
                </button>
              )}
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setJsonModal(record)}>
                View JSON
              </button>
            </div>
          );
        },
      },
    ],
    [bulkSyncState.isRunning, canSyncRecord, getRecordKey, openSyncPreview, syncingRecordIds],
  );

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label htmlFor="selected-date" style={{ marginBottom: 0 }}>HS Updated At:</label>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ height: '30px' }}
                title="Previous day"
                aria-label="Previous day"
                onClick={() => {
                  const newStartDate = DateTime.fromISO(getStartDate()).minus({ days: 1 }).startOf('day').toISO().slice(0, 16);
                  const newEndDate = DateTime.fromISO(newStartDate).plus({ days: 1 }).toISO().slice(0, 16);
                  setDateParams(newStartDate, newEndDate);
                }}
              >
                &#8592;
              </button>
              <input
                id="selected-date"
                type="date"
                style={{ height: '30px' }}
                value={getSelectedDate()}
                onChange={(event) => {
                  if (!event.target.value) return;

                  const newStartDate = DateTime.fromISO(event.target.value).startOf('day').toISO().slice(0, 16);
                  const newEndDate = DateTime.fromISO(newStartDate).plus({ days: 1 }).toISO().slice(0, 16);
                  setDateParams(newStartDate, newEndDate);
                }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ height: '30px' }}
                title="Next day"
                aria-label="Next day"
                onClick={() => {
                  const newStartDate = DateTime.fromISO(getStartDate()).plus({ days: 1 }).startOf('day').toISO().slice(0, 16);
                  const newEndDate = DateTime.fromISO(newStartDate).plus({ days: 1 }).toISO().slice(0, 16);
                  setDateParams(newStartDate, newEndDate);
                }}
              >
                &#8594;
              </button>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {requestLog.some((entry) => entry.pending) ? (
            <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
              Loading...
            </div>
          ) : loadTime ? (
            <>
              <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
                Loaded in {loadTime}s
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ height: '30px' }}
                onClick={refreshData}
              >
                Reload Data
              </button>
            </>
          ) : null}
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          {requestLog.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setRequestLogModalOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#0d6efd',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
              </button>
              <RequestLogBadge requestLog={requestLog} />
            </div>
          )}
        </div>
      </div>

      {requestLogModalOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setRequestLogModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Chunk</th>
                          <th>Bucket range</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Scanned</th>
                          <th>Returned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requestLog.map((entry) => (
                          <tr
                            key={entry.requestNumber}
                            style={
                              entry.pending ? { backgroundColor: 'rgba(255, 193, 7, 0.1)' }
                              : entry.ok === false ? { backgroundColor: 'rgba(255, 0, 0, 0.08)' }
                              : undefined
                            }
                          >
                            <td>{entry.requestNumber}</td>
                            <td>{entry.chunkIndex != null ? `${entry.chunkIndex + 1}/${entry.totalChunks}` : entry.offset}</td>
                            <td>
                              {entry.bucketStart && entry.bucketEnd
                                ? `${DateTime.fromISO(entry.bucketStart).toFormat('MM/dd HH:mm')} – ${DateTime.fromISO(entry.bucketEnd).toFormat('MM/dd HH:mm')}`
                                : '—'}
                            </td>
                            <td>
                              {entry.pending
                                ? 'Pending...'
                                : entry.ok ? entry.status : `${entry.status || 'ERR'}${entry.error ? ` (${entry.error})` : ''}`}
                            </td>
                            <td>{entry.pending ? '—' : `${entry.durationMs}ms`}</td>
                            <td>{entry.scanned ?? '—'}</td>
                            <td>{entry.returned ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setRequestLogModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setRequestLogModalOpen(false)} />
        </>
      )}

      <div className="border bg-white p-3">
        <div className="border" style={{ flexDirection: 'row', display: 'flex' }}>
          <div style={{ padding: '10px', width: '50%' }}>
            <strong>Sync Status:</strong> <span className="text-muted" style={{ fontSize: '12px' }}>(click a status to filter)</span>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('hs_only')}
              title="Filter to hs_only records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.hs_only || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>hs_only</b> Record exists in Hubspot but not in Fastlane
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('ff_only')}
              title="Filter to ff_only records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.ff_only || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>ff_only</b> Record exists in Fastlane but not in Hubspot
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('out_of_sync')}
              title="Filter to out_of_sync records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>{statusCounts.out_of_sync || 0}</b>
              <b className="border px-1" style={{ backgroundColor: '#f5f5f5', borderRadius: '3px' }}>out_of_sync</b> Record exists in both systems but has real changes beyond the timestamp
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('out_of_sync_timestamp_only')}
              title="Filter to out_of_sync_timestamp_only records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'rgba(0, 123, 255, 0.05)', borderRadius: '3px' }}>{statusCounts.out_of_sync_timestamp_only || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'rgba(0, 123, 255, 0.05)', borderRadius: '3px' }}>out_of_sync_timestamp_only</b> HubSpot's timestamp changed but nothing we track did — safe to ignore
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('synced')}
              title="Filter to synced records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'white', borderRadius: '3px' }}>{statusCounts.synced || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'white', borderRadius: '3px' }}>synced</b> Record exists in both systems and is in sync
            </div>
          </div>
        </div>

        {loadError && (
          <div className="alert alert-danger mb-3" role="alert">
            {loadError}
          </div>
        )}

        <Table
          loadingFFData={loadingFFData}
          data={combinedData}
          columns={columns}
          autoResetPageIndex={false}
          renderToolbar={(table) => {
            tableInstanceRef.current = table;

            const pageSyncableRecords = table.getRowModel().rows
              .map((row) => row.original)
              .filter(canSyncRecord);

            const showSyncTimestampsOnly = table.getColumn('sync_status')?.getFilterValue() === 'out_of_sync_timestamp_only';

            const pageTimestampOnlyRecords = table.getRowModel().rows
              .map((row) => row.original)
              .filter((row) => row.sync_status === 'out_of_sync_timestamp_only' && canSyncRecord(row));

            return (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={bulkSyncState.isRunning || pageSyncableRecords.length === 0}
                  onClick={() => openBulkSyncConfirmation(pageSyncableRecords)}
                >
                  {bulkSyncState.isRunning
                    ? `Syncing Page (${bulkSyncState.completed}/${bulkSyncState.total})`
                    : `Sync This Page (${pageSyncableRecords.length})`}
                </button>
                {showSyncTimestampsOnly && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={bulkTimestampSyncState.isRunning || pageTimestampOnlyRecords.length === 0}
                    onClick={() => openBulkTimestampSyncConfirmation(pageTimestampOnlyRecords)}
                    title="Updates only the HubSpot timestamp, in a single request, for records where nothing this app tracks has actually changed"
                  >
                    {bulkTimestampSyncState.isRunning
                      ? 'Syncing Timestamps...'
                      : `Sync Timestamps Only (${pageTimestampOnlyRecords.length})`}
                  </button>
                )}
              </div>
            );
          }}
        />
      </div>

      {bulkSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Bulk Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Sync {bulkSyncConfirmation.records.length} records from the current page?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This will send one request per row and open the progress modal immediately after you confirm.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkSync}>
                    Start Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkSyncConfirmation} />
        </>
      )}

      {bulkTimestampSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Timestamp Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkTimestampSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Update the HubSpot timestamp for {bulkTimestampSyncConfirmation.records.length} record{bulkTimestampSyncConfirmation.records.length === 1 ? '' : 's'} from the current page?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This sends a single batch request and updates only the lastUpdate column for each record — no other fields are touched. Each record is re-verified on the server before its timestamp is updated, and any record found to have real changes is skipped rather than synced.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkTimestampSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkTimestampSync}>
                    Sync Timestamps
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkTimestampSyncConfirmation} />
        </>
      )}

      {bulkSyncState.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Bulk Sync Progress</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeBulkSyncModal}
                    aria-label="Close"
                    disabled={bulkSyncState.isRunning}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{bulkSyncState.completed} of {bulkSyncState.total} complete</strong>
                      <span>{bulkProgressPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: '12px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${bulkProgressPercent}%` }}
                        aria-valuenow={bulkProgressPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div style={{ marginTop: '6px' }}>
                      {bulkSyncState.isRunning
                        ? `Syncing ${bulkSyncState.currentLabel || 'next lead'}...`
                        : 'Bulk sync complete.'}
                    </div>
                  </div>

                  <div>
                    <strong>Failures ({bulkSyncState.failures.length})</strong>
                    <div
                      className="border rounded mt-2 p-2"
                      style={{ maxHeight: '260px', overflowY: 'auto', backgroundColor: '#fafafa' }}
                    >
                      {bulkSyncState.failures.length === 0 ? (
                        <div className="text-muted">No failures yet.</div>
                      ) : (
                        bulkSyncState.failures.map((failure) => (
                          <div key={failure.id} className="border-bottom pb-2 mb-2">
                            <div><strong>{failure.label}</strong></div>
                            <div style={{ whiteSpace: 'pre-line' }}>{failure.message}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeBulkSyncModal}
                    disabled={bulkSyncState.isRunning}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={bulkSyncState.isRunning ? undefined : closeBulkSyncModal} />
        </>
      )}

      {/* Sync Preview Modal */}
      {syncPreview.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {syncPreview.isNewRecord ? 'Create Lead From HubSpot' : 'Sync Lead With HubSpot'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSyncPreview}
                    aria-label="Close"
                    disabled={syncPreview.isSaving}
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {syncPreview.isLoading && <div className="text-muted">Loading preview...</div>}

                  {syncPreview.error && (
                    <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                      {syncPreview.error}
                    </div>
                  )}

                  {!syncPreview.isLoading && !syncPreview.error && syncPreview.fields && (
                    <>
                      {!syncPreview.fields.some((field) => field.changed) && (
                        <div className="text-muted mb-2">No changes — this record already matches HubSpot.</div>
                      )}
                      <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Property</th>
                            <th>Old Value</th>
                            <th>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncPreview.fields.map(({ property, from, to, changed }) => (
                            <tr key={property}>
                              <td>{humanizeDiffKey(property)}</td>
                              <td>{formatDiffValue(from)}</td>
                              <td style={changed ? { backgroundColor: 'rgba(40, 167, 69, 0.2)', fontWeight: 600 } : undefined}>
                                {formatDiffValue(to)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeSyncPreview}
                    disabled={syncPreview.isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmSyncPreview}
                    disabled={syncPreview.isLoading || syncPreview.isSaving || Boolean(syncPreview.error) || !(syncPreview.fields || []).some((field) => field.changed)}
                  >
                    {syncPreview.isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={syncPreview.isSaving ? undefined : closeSyncPreview} />
        </>
      )}

      {/* Error Modal */}
      {modalMessage && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Sync Failed</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setModalMessage(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{modalMessage}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalMessage(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setModalMessage(null)} />
        </>
      )}

      {/* JSON Viewer Modal */}
      {jsonModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Record JSON</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setJsonModal(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(jsonModal, null, 2)}
                  </pre>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setJsonModal(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setJsonModal(null)} />
        </>
      )}

      {successToastMessage && (
        <div
          className="alert alert-success shadow"
          role="status"
          style={{
            position: 'fixed',
            right: '16px',
            bottom: '16px',
            margin: 0,
            zIndex: 2000,
            minWidth: '280px',
            maxWidth: '420px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{successToastMessage}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccessToastMessage(null)}
          />
        </div>
      )}


    </>
  );
}

const element = document.getElementById('leads-table');
if (element) {
  const root = createRoot(element);
  root.render(<LeadsTable />);
}